"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { X } from "lucide-react";

import { IconButton } from "@/components/scirix/icon-button";
import { cn } from "@/lib/utils";

export type TabElementState = "open" | "closed";
export type TabElementInteractionState = "idle" | "hover" | "inFocus";

export interface TabElementProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  state?: TabElementState;
  label?: React.ReactNode;
  /**
   * Optional className applied to the outer wrapper span so layout containers can
   * control flex behaviour without adding extra DOM nodes.
   */
  wrapperClassName?: string;
  /**
   * Reserved interaction state so hover/focus styles can be customized later.
   * Defaults to tracking real hover/focus events.
   */
  interactionState?: TabElementInteractionState;
  /**
   * Optional Lucide icon rendered to the left of the label. Defaults to the workspace placeholder icon.
   */
  icon?: LucideIcon;
  /**
   * Toggle to hide the icon slot when a tab doesn't need glyphs.
   */
  showIcon?: boolean;
  /**
   * When true (or when onClose is provided) renders the Mini IconButton close affordance on the right side.
   */
  closable?: boolean;
  /**
   * Optional override for the close button label. Defaults to `Close ${label}`.
   */
  closeButtonAriaLabel?: string;
  /**
   * Handler invoked when the close button is activated.
   */
  onClose?: (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => void;
  children?: React.ReactNode;
}

export const TabElement = React.forwardRef<HTMLButtonElement, TabElementProps>(
  (
    {
      state = "open",
      label,
      wrapperClassName,
      interactionState,
      icon,
      showIcon = true,
      closable,
      closeButtonAriaLabel,
      onClose,
      className,
      role,
      type = "button",
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      style,
      disabled = false,
      ["aria-selected"]: ariaSelectedProp,
      ["aria-pressed"]: ariaPressedProp,
      children,
      ...props
    },
    ref
  ) => {
    const [internalInteraction, setInternalInteraction] =
      React.useState<TabElementInteractionState>("idle");

    const resolvedInteraction = interactionState ?? internalInteraction;
    const interactionAttr = resolvedInteraction === "inFocus" ? "focus" : resolvedInteraction;

    const isOpen = state === "open";
    const resolvedLabel = label ?? (isOpen ? "Workspace" : "workspace");
    const shouldRenderCloseButton = (closable ?? Boolean(onClose)) && Boolean(onClose);
    const resolvedCloseLabel =
      closeButtonAriaLabel ??
      (typeof resolvedLabel === "string" ? `Close ${resolvedLabel}` : "Close tab");

    const IconComponent = icon;
    const iconPlaceholder = (
      <span
        aria-hidden
        className="block h-2.5 w-2.5 rounded-[0.35rem] border border-current/30"
        data-state={state}
      />
    );
    const renderedIcon = showIcon ? (
      <span
        className="tab-element__icon inline-flex h-4 w-4 items-center justify-center text-muted-foreground data-[state=open]:text-foreground/90"
        data-state={state}
      >
        {IconComponent ? (
          <IconComponent
            aria-hidden
            className="h-3.5 w-3.5"
            data-state={state}
          />
        ) : (
          iconPlaceholder
        )}
      </span>
    ) : null;

    const renderedChildren =
      children ?? (
        <span
          className="flex min-w-0 flex-1 items-center gap-[var(--space-sm)] text-left leading-none"
          data-state={state}
        >
          {renderedIcon}
          <span className="truncate leading-[20px]" data-state={state}>
            {resolvedLabel}
          </span>
        </span>
      );
    const computedRole = role ?? "tab";

    const background = isOpen ? "var(--token-surface-default)" : "var(--token-surface-muted)";
    // Active tab keeps the panel's background on hover/focus so the merge into
    // the content panel stays seamless. Only inactive tabs get the interactive
    // color-mix feedback.
    const hoverBackground = isOpen
      ? "var(--token-surface-default)"
      : "color-mix(in oklab, var(--token-surface-muted) 92%, var(--token-foreground-default) 8%)";

    const ariaSelected = computedRole === "tab" ? ariaSelectedProp ?? isOpen : ariaSelectedProp;
    const ariaPressed = computedRole !== "tab" ? ariaPressedProp ?? isOpen : ariaPressedProp;

    const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!interactionState) {
        setInternalInteraction("hover");
      }
      onMouseEnter?.(event);
    };

    const handleMouseLeave = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!interactionState) {
        setInternalInteraction("idle");
      }
      onMouseLeave?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
      if (!interactionState) {
        setInternalInteraction("inFocus");
      }
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
      if (!interactionState) {
        setInternalInteraction("idle");
      }
      onBlur?.(event);
    };

    const handleClose = (event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (disabled) return;
      onClose?.(event);
    };

    const handleCloseKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      handleClose(event);
    };

    return (
      <span
        className={cn(
          "tab-element__group relative inline-flex w-full min-w-0 items-center",
          wrapperClassName,
        )}
      >
        <button
          {...props}
          ref={ref}
          type={type}
          role={computedRole}
          aria-selected={ariaSelected}
          aria-pressed={ariaPressed}
          data-state={state}
          data-interaction={interactionAttr}
          data-hover={interactionAttr === "hover" ? true : undefined}
          data-focus={interactionAttr === "focus" ? true : undefined}
          disabled={disabled}
          className={cn(
            "tab-element inline-flex w-full flex-1 items-center justify-start gap-[var(--space-sm)] pl-[var(--space-lg)] py-[var(--space-sm)] text-left text-sm transition-colors",
            "data-[state=closed]:py-[6px]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-0",
            isOpen
              ? "rounded-t-[var(--radius-xxl)] rounded-b-none bg-[var(--tab-element-bg)] text-foreground font-semibold"
              : "rounded-[var(--radius-xxl)] bg-[var(--tab-element-bg)] text-muted-foreground font-normal",
            "hover:bg-[var(--tab-element-hover-bg)] focus-visible:shadow-[var(--shadow-sm)]",
            "data-[interaction=hover]:bg-[var(--tab-element-hover-bg)] data-[interaction=focus]:bg-[var(--tab-element-hover-bg)]",
            "data-[interaction=focus]:shadow-[var(--shadow-sm)]",
            "disabled:cursor-not-allowed disabled:opacity-60",
            className
          )}
          style={{
            ["--tab-element-bg" as string]: background,
            ["--tab-element-hover-bg" as string]: hoverBackground,
            paddingRight: shouldRenderCloseButton ? `calc(var(--space-lg) + 1.25rem)` : "var(--space-lg)",
            ...style,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {renderedChildren}
        </button>
        {shouldRenderCloseButton ? (
          <span
            data-tab-element-close-slot="true"
            className="tab-element__close-slot absolute inset-y-0 right-[var(--space-sm)] flex items-center"
          >
            <IconButton
              ariaLabel={resolvedCloseLabel}
              icon={X}
              onClick={handleClose}
              onKeyDown={handleCloseKeyDown}
              disabled={disabled}
              className={cn(
                "tab-element__close h-6 w-6 pointer-events-auto text-muted-foreground/80",
                "data-[tab-state=open]:text-foreground/90"
              )}
              data-tab-state={state}
            />
          </span>
        ) : null}
      </span>
    );
  }
);

TabElement.displayName = "TabElement";

export default TabElement;
